export enum ImageProcessingStatus {
  Pending = 'pending',
  Downloading = 'downloading',
  CreatingVariants = 'creating_variants',
  ValidatingDimensions = 'validating_dimensions',
  DimensionsOk = 'dimensions_ok',
  DimensionsError = 'dimensions_error',
  PreparingToCutTiles = 'preparing_to_cut_tiles',
  CuttingTiles = 'cutting_tiles',
  UploadingTiles = 'uploading_tiles',
  ErrorGenerating = 'error_generating',
  Generated = 'generated',
}

export enum AssetTypes {
  IMAGE = 'image',
  VIDEO = 'video',
}

export interface BackgroundLod {
  pageSize: number;
  pagesHigh: number;
  pagesWide: number;
  permanentLod: number;
  pageBorderWidth: number;

  // TODO: verify if these are present in editor API responses
  width: number;
  height: number;
  worstLod: number; // worst LOD of background, not sphere items
  x: number;
  y: number;
}

export interface BackgroundImage {
  id: number;
  path: string;
  url: string;
  image_name: string;
  tiles_path: string;
  status: ImageProcessingStatus;
  original_width: number;
  original_height: number;

  // TODO: available only in editor?
  thumbnails?: Thumbnails;
  groupedStatus?: GroupedImageProcessingStatus;
}

export interface BackgroundImageWithLod extends BackgroundImage {
  virtual_params?: BackgroundLod;
}

export interface GradientPoint {
  x: number;
  y: number;
  color: PalettedColor;
  opacity: number;
}

export interface Gradient {
  points: GradientPoint[];
  blendPower: number;
}

export interface PlanogramClusterData extends PlanogramShapeData {
  clusterLink: string;
  originalClusterLink: string;
  clusterMode: ClusterMode;
  padding: ClusterPadding;
  clusterUrlManuallyUpdated: boolean;
  clusterSeoFields: ClusterSeoFields;
  accessibility: Accessibility;
  verticalAlignment: Alignment;
  horizontalAlignment: Alignment;
  distributeEqually: boolean;
  equalSizeObjects: boolean;
  individualPadding: boolean;
  spacing: number;
}

export enum FitMode {
  Fit = 0,
  Fill = 1,
  Stretch = 2,
  Crop = 3,
}

export interface PlanogramImageData {
  id: number;
  naturalResolution: [number, number]; // used only to display sidebar info
  image_name: string;
  accessibility?: Accessibility;
  thumbnails: Thumbnails | undefined;
  isDeleted?: boolean; // TODO: should not be written by editor?
  opacity: number;
  shading?: PalettedColor;
  isFigma?: boolean;
  cropOffset?: [number, number];
  cropRatio?: [number, number];
  fitMode?: FitMode;
  previousFitMode?: FitMode;
  cornerRadius?: [number, number, number, number];
}

export type PlanogramProductData = PlanogramImageData & {
  picture: PictureData;
  product?: Product;
};

export interface PlanogramTextAreaData {
  alignment: TextHorizontalAlignment;
  alpha: number;
  color: PalettedColor;
  fontFamily: Font;
  fontSize: number;
  letterSpacing: number;
  lineHeight: number;
  text: string;
  weight: string;
  verticalAlignment: TextVerticalAlignment;
  resizingMode: TextSizingMode;
  autoFit: boolean;
  effectiveFontSize: number;
  effectiveLineHeight: number;
  overflowWrap: 'normal' | 'break-word';
  maxRows?: number;
}

export interface PlanogramVideoData {
  autoplay: boolean;
  hideControls: boolean;
  loop: boolean;
  videoUrl: string;
  poster: VideoPoster | undefined;
  accessibility: Accessibility | undefined;
  share: boolean | undefined;
  maskColor: PalettedColor | undefined;
  maskImage: TransparencyMask | undefined;
  maskSensitivity?: [number, number];
  opacity: number | undefined;
  shading: PalettedColor | undefined;
  fullSize: boolean | undefined;
  naturalResolution?: [number, number];
  cornerRadius: [number, number, number, number] | undefined;
  fitMode: FitMode | undefined;
  previousFitMode: FitMode | undefined;
  cropOffset: [number, number] | undefined;
  cropRatio: [number, number] | undefined;
}

export enum BorderSide {
  Inside,
  Centered,
  Outside,
}

export interface PlanogramShapeData {
  cornerRadius?: number;
  individualCornerRadius: [number, number, number, number];
  indentation?: number;
  borderColor: PalettedColor;
  borderOpacity: number;
  fillColor: PalettedColor;
  fillOpacity: number;
  thickness: number;
  borderSide: BorderSide;

  // TODO: drop these two entirely
  dashed: boolean;
  soft: boolean;
}

export interface PlanogramCurveData {
  color: PalettedColor;
  opacity: number;
  points: number[];
  soft: boolean;
  thickness: number;
  dashed: boolean;
  scale: [number, number, number];
}

type BaseSocialFeedData = {
  source?: SocialFeedSource;
  feedType?: FeedType;
  aspectRatio?: number;
};

type PlanogramImageFeedData = BaseSocialFeedData & PlanogramImageData & { imageId?: number };
type PlanogramVideoFeedData = BaseSocialFeedData & PlanogramVideoData;

export type PlanogramSocialFeedData =
  | ({ feedType?: undefined } & BaseSocialFeedData)
  | ({ feedType: FeedType.Image } & PlanogramImageFeedData)
  | ({ feedType: FeedType.Video } & PlanogramVideoFeedData);

export type PlanogramItemData =
  | PlanogramClusterData
  | PlanogramImageData
  | PlanogramProductData
  | PlanogramTextAreaData
  | PlanogramVideoData
  | PlanogramShapeData
  | PlanogramCurveData
  | PlanogramSocialFeedData;

export interface PlanogramVersionItem<T = PlanogramItemData> {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  type: SphereItemType;
  name: string;
  layer: number;
  data: T;
  tag?: string; // used to keep track of Product Name and SKU
  childrenIds?: string[];
  lockedParent?: boolean; // used to keep track of non-removeable objects like product Name and SKU
  action?: Action;
  lockedAspectRatio?: boolean;
  builderData?: BuilderData;
  visible: boolean;
}

export interface PlanogramItem {
  id: number;
  name: string;
  updated_at: string;
  creator: string;
  items_count: number;
  language_code: string;
  planogram_versions_statuses: String[];
}

export interface AnimationSettings {
  duration: number;
  autoplay_delay: number;
  transition_type: AnimationTransitionType;
  pan_before_zoom: boolean;
}

export interface PlanogramVersionBase {
  animation_settings: AnimationSettings;
  id: number;
  version: number;
  status: PlanogramStatus;
}

// TODO: merge with viewer/src/app/planogram.interface.ts
export interface PlanogramVersion extends PlanogramVersionBase {
  updated_at: string; // "11 Jun,  2:53PM, 2020"
  created_at: string; // "11 Jun,  2:53PM, 2020"
  fabric_state: Partial<FabricState>;
  curator_update_error: string;
  curator_update_status: 'failed' | 'success' | 'in_progress';
  step: string;
  temporary_login: string | null;
  temporary_password: string | null;
  generated_login: string | null;
  generated_password: string | null;
  url: string;
  navigation_arrow?: {
    animation_path?: AnimationPath;
  };
}

export interface PlanogramGroup {
  id: number;
  name: string;
  updated_at: string;
  is_landing: boolean;
  creator: string;
  language_codes: String[];
  client_subdomain_id: number;
  planograms: PlanogramItem[];
}

export interface StripePaywallCondition {
  type: 'stripe';
  productId: string;
}

// stack of polish notation conditions
// condition "types" such as "conjuction/and", "disjunction/or", "negation"?, "verum/true"
export type PaywallStackItem = StripePaywallCondition;

interface PaywallConditions0 {
  version: undefined;
}

export interface PaywallConditions1 {
  version: 1;
  stack: PaywallStackItem[];
}

export type PaywallConditions = PaywallConditions0 | PaywallConditions1;

export interface RawPaywallData {
  active: boolean;
  conditions: PaywallConditions;
}

export type PaywallData = RawPaywallData & { conditions: PaywallConditions1 };

export interface PlanogramData {
  id: number;
  name: string;
  seo_desc: string | null;
  planogram_versions: PlanogramVersion[];
  hosts: string[];
  sphere_template: Template;
  pl_status: 'success' | 'processing' | null;
  paywall?: RawPaywallData;
}

export interface Template {
  id: number;
  default_template_id: number;
  demo_sphere_url: string;
  description: string;
  is_default: boolean;
  is_enabled: boolean;
  title: string;
  picture_id: number;
  planogram_id: number;
  picture: PictureData;
}

export type AnimationTransitionType =
  | 'linear'
  | 'easeIn'
  | 'easeOut'
  | 'easeInOut'
  | 'bounce'
  | 'instant';

export interface AnimationPathItem {
  name: string;
  planogramName: string;
  language: string;
  itemId: string;
  transitionType: AnimationTransitionType;
  panBeforeZoom: boolean;
  duration: number;
  delay: number;
  zoomLevel: number;
  showSeoTitle?: boolean;
  action?: Action;
}

export enum AnimationPathType {
  Custom = 'custom',
  NavigationArrows = 'navigation-arrows',
  Journey = 'journey',
}

export interface AnimationPath {
  uuid: string;
  name: string;
  type: AnimationPathType;
  items: AnimationPathItem[];
  autoplay?: boolean;
  applyFinalItemsAction?: boolean;
  loop?: boolean;
  startWithoutNavigating?: boolean;
  usedForNavArrows?: boolean;
  seoTitle: string;
  seoDescription: string;
}

export interface Font {
  id: number;
  file_url: string;
  name: string;
  filename: string;
  is_system: boolean | null;
  is_default: boolean | null;
  font_family: string;
  font_weight: string | null;
  computedWeight: string | null;
  display_font_family: string;
}

export interface FontWeightOption {
  fontId: number;
  name: string;
  weight: string;
  file_url: string;
}

export interface TemplateFont {
  regular: FontWeightOption;
  bold: FontWeightOption;
}

export type TemplateFontMappingType = Record<TemplateFontStyleOption, TemplateFont>;

export interface PaletteColor {
  name: string;
  color: string;
}

export interface Palette {
  name: string;
  colors: PaletteColor[];
  custom?: boolean;
}

export type PalettedColor = string | number; // string is a hex color, number is a color index from the palette

interface SphereConfig {
  horizonLine: number;
  startingPoint: number;
  backgroundColor: PalettedColor;
  backgroundImageId?: number;
  backgroundImage?: BackgroundImage;
  backgroundGradient?: Gradient;
  clustersOrder: string[];
  iframesOrder: string[];
  animationPaths: AnimationPath[];
  palette?: Palette; // assigned in builder, but colors are resolved both in builder and viewer
}

interface EditorConfig {
  templateFontMappings: TemplateFontMappingType | undefined;
  palettes?: Palette[]; // default state is undefined, so that we know when to add the default palettes
  showGrid: boolean;
  disableClusters: boolean;
  lockedObjects: string[];
}

export interface FabricState {
  items: PlanogramVersionItem[];
  figmaBackground: { id: string; items: PlanogramVersionItem[] } | undefined;
  sphereConfig: SphereConfig;
  editorConfig: EditorConfig;
  initial_cloning?: boolean;
}

export interface PlanogramDataToSave {
  planogram_version: {
    fabric_state: FabricState;
  };
}

export interface FigmaBackgroundAsset {
  uuid: string;
  x: number;
  y: number;
  width: number;
  height: number;
  imageId: number;
}

export interface FigmaBackground {
  id: number;
  uuid: string;
  name: string;
  thumbnails: Thumbnails;
  background_color: string;
  items: FigmaBackgroundAsset[];
  image_lods_status: GroupedImageProcessingStatus.Pending | GroupedImageProcessingStatus.Generated;
}

export function isPlanogramVersionImage(
  item: PlanogramVersionItem,
): item is PlanogramVersionItem<PlanogramImageData> {
  return item.type === SphereItemType.Image;
}
export function isPlanogramVersionCluster(
  item: PlanogramVersionItem,
): item is PlanogramVersionItem<PlanogramClusterData> {
  return item.type === SphereItemType.Cluster;
}
export function isPlanogramVersionProduct(
  item: PlanogramVersionItem,
): item is PlanogramVersionItem<PlanogramProductData> {
  return item.type === SphereItemType.Product;
}
export function isPlanogramVersionTextArea(
  item: PlanogramVersionItem,
): item is PlanogramVersionItem<PlanogramTextAreaData> {
  return item.type === SphereItemType.TextArea || item.type === SphereItemType.Text;
}
export function isPlanogramVersionVideo(
  item: PlanogramVersionItem,
): item is PlanogramVersionItem<PlanogramVideoData> {
  return item.type === SphereItemType.Video;
}
export function isPlanogramVersionShape(
  item: PlanogramVersionItem,
): item is PlanogramVersionItem<PlanogramShapeData> {
  return item.type === SphereItemType.Shape;
}
export function isPlanogramVersionCurve(
  item: PlanogramVersionItem,
): item is PlanogramVersionItem<PlanogramCurveData> {
  return item.type === SphereItemType.Curve;
}
export function isPlanogramVersionGroup(item: PlanogramVersionItem): item is PlanogramVersionItem {
  return item.type === SphereItemType.Group;
}

export function isPlanogramVersionSocialFeed(
  item: PlanogramVersionItem,
): item is PlanogramVersionItem<PlanogramSocialFeedData> {
  return item.type === SphereItemType.SocialFeed;
}
export interface Accessibility {
  title?: string;
  description?: string;
}

export enum BuilderObjectType {
  Panel = 'panel',
  Button = 'button',
  SocialIcons = 'social_icons',
  Video = 'video',
  Image = 'image',
  Media = 'media',
  ButtonList = 'button_list',
}

export interface BuilderData {
  objectType?: BuilderObjectType;
  templateObject?: PlanogramVersionItem;
}

export enum ActionType {
  AboutUs = 'about-us',
  Animate = 'animate',
  Audio = 'audio',
  ContactUs = 'contact-us',
  ContentOverlay = 'iframe',
  CookiesPolicy = 'cookies-policy',
  ExternalLink = 'external-link',
  IframeLink = 'iframe-link',
  LegalNotice = 'copyright',
  PrivacyPolicy = 'privacy-policy',
  ProductOverlay = 'product-overlay',
  SingleImage = 'single-image',
  SocialLink = 'social-link',
  VideoOverlay = 'video-overlay',
  CarouselOverlay = 'content-carousel',
  CookiesPopup = 'cookies-popup',
}

// TODO: have a better way to express in-progress actions in curator than undefined data, such as storing it in UI state
export interface Action<T = ActionData | undefined> {
  type: ActionType;
  data: T;
}

export interface LinkActionData {
  url: string;
  sameTab?: true;
}

export function isLinkAction(action: Action): action is Action<LinkActionData> {
  return (
    action.data !== undefined &&
    (action.type === ActionType.ExternalLink || action.type === ActionType.IframeLink)
  );
}

export interface ProductOverlayActionData {
  // TODO: validate if these can be undefined
  productId?: number;
  productName?: string;
  productIdentifier?: string;
  savedBy?: SearchAttribute;
  isSaved?: boolean;
}

export function isProductOverlayAction(action: Action): action is Action<ProductOverlayActionData> {
  return action.data !== undefined && action.type === ActionType.ProductOverlay;
}

export interface VideoOverlayActionData {
  videoId?: number;
  url: string;
}

export interface CarouselOverlayActionData {
  items: CarouselAssets[];
}

export interface CarouselAssets {
  contentType: string;
  seoTitle: string;
  assetType: AssetTypes;
  url: string;
  thumbnails?: Thumbnails;
  thumbnailUrl?: string;
}

export function isVideoOverlayAction(action: Action): action is Action<VideoOverlayActionData> {
  return action.data !== undefined && action.type === ActionType.VideoOverlay;
}

export function isCarouselOverlay(action: Action): action is Action<CarouselOverlayActionData> {
  return action.data !== undefined && action.type === ActionType.CarouselOverlay;
}

export interface ContentOverlayActionData {
  url: string;
  iframeLink: string;
  transparent: boolean;
}

export function isContentOverlayAction(action: Action): action is Action<ContentOverlayActionData> {
  return action.data !== undefined && action.type === ActionType.ContentOverlay;
}

export enum SocialFeedSource {
  Instagram = 'Instagram',
  Facebook = 'Facebook',
  X = 'X',
  YouTube = 'Youtube',
  TikTok = 'TikTok',
  Snapchat = 'Snapchat',
  Twitch = 'Twitch',
  Linkfire = 'Linkfire',
}

export interface SocialLinkActionData {
  contentValue: string; // the actual url from PubTool
  source: SocialFeedSource; // social media type
  contentType: string; // content type from PubTool
}

export function isSocialLinkAction(action: Action): action is Action<SocialLinkActionData> {
  return action.data !== undefined && action.type === ActionType.SocialLink;
}

export interface AnimateActionData {
  sphereName?: string; // actions sphere name to link to
  itemId?: string;
  applyItemsActionInTheEnd?: boolean; // if after animation the action should be fired on the element
  productId?: number;
  productName?: string;
  productIdentifier?: string;
  clusterLink?: string;
  itemType?: SphereItemType;
  imageName?: string;
  imageId?: number;
  name?: string;
  showSeoTitle?: boolean;
  destinationAction?: Action;
}

export function isAnimateAction(action: Action): action is Action<AnimateActionData> {
  return action.data !== undefined && action.type === ActionType.Animate;
}

export interface AudioActionData {
  id: number;
  url: string;
}

export function isAudioAction(action: Action): action is Action<AudioActionData> {
  return action.data !== undefined && action.type === ActionType.Audio;
}

export function isSingleImageAction(action: Action): action is Action<undefined> {
  return action.type === ActionType.SingleImage;
}

export function isInfoOverlayAction(action: Action): action is Action<undefined> {
  return (
    action.type === ActionType.AboutUs ||
    action.type === ActionType.ContactUs ||
    action.type === ActionType.CookiesPolicy ||
    action.type === ActionType.LegalNotice ||
    action.type === ActionType.PrivacyPolicy
  );
}

export function isCookiePopupAction(action: Action): action is Action<undefined> {
  return action.type === ActionType.CookiesPopup;
}

export type ActionData =
  | LinkActionData
  | ProductOverlayActionData
  | VideoOverlayActionData
  | ContentOverlayActionData
  | SocialLinkActionData
  | AnimateActionData
  | AudioActionData
  | CarouselOverlayActionData;

export function isOverlayAction(action: Action): boolean {
  return (
    action.type === ActionType.ProductOverlay ||
    action.type === ActionType.VideoOverlay ||
    action.type === ActionType.SingleImage ||
    action.type === ActionType.ContentOverlay ||
    action.type === ActionType.ContactUs ||
    action.type === ActionType.CookiesPolicy ||
    action.type === ActionType.LegalNotice ||
    action.type === ActionType.PrivacyPolicy ||
    action.type === ActionType.AboutUs ||
    action.type === ActionType.SocialLink ||
    action.type === ActionType.CarouselOverlay ||
    action.type === ActionType.IframeLink
  );
}

export interface ClusterSeoFields {
  title?: string;
  h1Tag?: string;
  description?: string;
}

export interface ClusterPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface PictureData {
  name?: string;
  seo_title?: string;
  accessibility_description?: string;
  curator_ready?: boolean;
  id: number;
  image_name: string | null;
  image_size: number;
  path: string;
  tags: string[];
  url: string;
  thumbnails: Thumbnails;
  status?: ImageProcessingStatus;
  groupedStatus?: GroupedImageProcessingStatus;
  last_error: string | null;
  used_on_planogram: boolean;
  original_width: number;
  original_height: number;
  selected?: boolean;
  already_on_canvas?: boolean;
  isDeleted: boolean;
  content_type: string;
}

export interface Product {
  accessibility_description?: string;
  id?: number;
  identifier?: string;
  name?: string;
  category_name?: string;
  pictures_used_on_planogram?: PictureData[];
  pictures_count?: number;
  updated_at?: string;
  picture?: PictureData;
  pictures?: PictureData[];
  search_matched_attributes?: SearchAttribute[];
}

export interface Thumbnails {
  VARIANT_LARGE_WEBP: string;
  VARIANT_MEDIUM_WEBP: string;
  VARIANT_SMALL_WEBP: string;
  VARIANT_THUMBNAIL_PNG: string;
  VARIANT_THUMBNAIL_JPEG: string;
  VARIANT_THUMBNAIL_WEBP: string;
  VARIANT_THUMBNAIL_PNG_600: string;
}

// only URL is used for direct link posters, the rest is mandatory for proper asset posters
export interface VideoPoster {
  url: string;
  naturalWidth?: number;
  naturalHeight?: number;
  thumbnails?: Thumbnails;
}

export interface TransparencyMask {
  url: string;
  naturalWidth: number;
  naturalHeight: number;
  thumbnails: Thumbnails;
}

export enum ClusterMode {
  Free = 'FREE',
  Vertical = 'VERTICAL',
  Horizontal = 'HORIZONTAL',
  Grid = 'GRID',
}

export enum GroupedImageProcessingStatus {
  Pending = 'pending',
  DimensionsOk = 'dimensions_ok',
  ErrorGenerating = 'error_generating',
  DimensionsError = 'dimensions_error',
  Generated = 'generated',
}

export enum PlanogramStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  GENERATED = 'generated',
  GENERATING = 'generating',
  PUBLISHED_DRAFTED = 'published_drafted',
  PUBLISHING = 'publishing',
}

export enum SearchAttribute {
  Identifier = 'identifier',
  Name = 'name',
}

export enum TextHorizontalAlignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export enum TextVerticalAlignment {
  TOP = 'top',
  MIDDLE = 'middle',
  BOTTOM = 'bottom',
}

export enum Alignment {
  START = 'start',
  CENTER = 'center',
  END = 'end',
}

export enum ClusterDistribution {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export enum TextSizingMode {
  FIXED = 'fixed',
  FIXED_WIDTH = 'fixed_width',
  FIXED_HEIGHT = 'fixed_height',
}

export enum SphereItemType {
  Default = 'DEFAULT',
  Group = 'GROUP', // TODO: this should be removed, and replaced with a cluster
  Image = 'IMAGE',
  Video = 'VIDEO',
  Text = 'TEXT',
  TextArea = 'TEXT_AREA',
  Particles = 'PARTICLES',
  Shape = 'SHAPE',
  Curve = 'CURVE',
  Cluster = 'CLUSTER',
  Product = 'PRODUCT',
  SocialFeed = 'SOCIAL_FEED',
  AnimationPath = 'ANIMATION_PATH', // TODO: this is not an item type
}

export enum FeedType {
  Image = 'Image',
  Video = 'Video',
}

export enum TemplateFontStyleOption {
  SIMPLE = 'simple',
  BOOKISH = 'bookish',
  TECHNICAL = 'technical',
  ELEGANT = 'elegant',
}

export enum TemplateFontWeightOption {
  REGULAR = 'regular',
  BOLD = 'bold',
}

export interface PlanogramObjectsResponse {
  clusters: PlanogramVersionItem[];
  curves: PlanogramVersionItem[];
  paths: { id: string }[];
  pictures: PlanogramVersionItem[];
  products: PlanogramVersionItem[];
  shapes: PlanogramVersionItem[];
  text_areas: PlanogramVersionItem[];
  videos: PlanogramVersionItem[];
}
